import { QueryClient } from 'react-query';
import { keys as projectKeys } from '../../project/keys';
import { Project, ProjectStackField } from '../../project';

type FieldTransformer = (field: ProjectStackField) => ProjectStackField;

type OptimisticUpdateContext = {
  previousProject?: Project;
};

/**
 * Shared optimistic update logic for field mapping mutations.
 * Handles cache cancellation, snapshotting, updating, and rollback.
 */
export const createOptimisticFieldMappingUpdate = async (
  queryClient: QueryClient,
  projectId: number,
  fieldTransformer: FieldTransformer,
): Promise<OptimisticUpdateContext> => {
  // Cancel any outgoing refetches
  await queryClient.cancelQueries(projectKeys.detail(projectId));

  // Snapshot the previous value
  const previousProject = queryClient.getQueryData<Project>(
    projectKeys.detail(projectId),
  );

  // Optimistically update the project cache
  if (previousProject) {
    queryClient.setQueryData<Project>(projectKeys.detail(projectId), {
      ...previousProject,
      stack_data: {
        ...previousProject.stack_data,
        fields: previousProject.stack_data.fields.map(fieldTransformer),
      },
    });
  }

  return { previousProject };
};

/**
 * Rolls back optimistic update on error
 */
export const rollbackOptimisticUpdate = (
  queryClient: QueryClient,
  projectId: number,
  previousProject?: Project,
): void => {
  if (previousProject) {
    queryClient.setQueryData(projectKeys.detail(projectId), previousProject);
  }
};
